import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import EmailDetail from "@/components/EmailDetail";
import ProComposeEmail from "@/components/ProComposeEmail";
import {
  Inbox, Archive, Star, AlertTriangle, LogOut, Settings, Shield, RefreshCw,
  Search, ArrowLeft, Copy, MailOpen, Menu, Loader2, PenLine, Send, Mail,
  Trash2, CheckSquare, Square, X, StarOff,
} from "lucide-react";
import MoveToMenu from "@/components/MoveToMenu";

/**
 * PRO Inbox — full email-client layout that reuses `EmailDetail` from the
 * main /inbox surface. Keeps the PRO auth system completely separate from
 * the main-site auth (uses /api/pro/* only, no AuthContext).
 */
const FOLDERS = [
  { key: "inbox", label: "Inbox", icon: Inbox, testId: "pro-folder-inbox" },
  { key: "sent", label: "Sent", icon: Send, testId: "pro-folder-sent" },
  { key: "archived", label: "Archived", icon: Archive, testId: "pro-folder-archived" },
  { key: "spam", label: "Spam", icon: AlertTriangle, testId: "pro-folder-spam" },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "starred", label: "Star" },
];

const POLL_INTERVAL_MS = 8000;

function parseUTC(dateStr) {
  if (!dateStr) return null;
  let s = String(dateStr).replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}

export default function ProInboxPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aliasAddress, setAliasAddress] = useState("");
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, starred: 0, archived: 0, spam: 0, sent: 0 });
  const [folder, setFolder] = useState("inbox");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filterLabelId, setFilterLabelId] = useState(null);
  const welcomeShown = useRef(false);
  const pollTimer = useRef(null);

  // ── Gate: redirect if not authenticated or not initialized ──
  useEffect(() => {
    (async () => {
      try {
        const { data: gate } = await api.get("/pro/gate-status");
        if (!gate.authenticated) {
          navigate("/pro/login", { replace: true });
          return;
        }
        if (!gate.initialized) {
          navigate("/pro/dashboard", { replace: true });
          return;
        }
        await loadMessages({ silent: false, folderOverride: "inbox", searchOverride: "" });
        loadCategoriesAndLabels();
        if (!welcomeShown.current) {
          welcomeShown.current = true;
          toast.success("Welcome to your inbox");
        }
      } catch (e) {
        navigate("/pro/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMessages = useCallback(async ({ silent, folderOverride, searchOverride } = {}) => {
    // Star is a status/filter, not a folder — when active, fetch the
    // backend's `starred` folder regardless of the user's folder selection.
    const baseFolder = folderOverride !== undefined ? folderOverride : folder;
    const f = filter === "starred" ? "starred" : baseFolder;
    const q = searchOverride !== undefined ? searchOverride : search;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const params = { folder: f };
      if (q) params.q = q;
      if (filterCategoryId) params.category_id = filterCategoryId;
      if (filterLabelId) params.label_id = filterLabelId;
      const { data } = await api.get("/pro/messages", { params });
      setAliasAddress(data.alias_address || "");
      setEmails(Array.isArray(data.emails) ? data.emails : []);
      setCounts(data.counts || { unread: 0, starred: 0, archived: 0, spam: 0, sent: 0 });
      // Reconcile selection with the freshly loaded rows — drop ids that
      // disappeared (deleted/moved/archived/spammed) but keep everything the
      // user is still working with. Never unconditionally clear here:
      // background polling must not steal selection (see bulk-actions spec).
      const newIds = new Set((Array.isArray(data.emails) ? data.emails : []).map((m) => m.email_id));
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev;
        let changed = false;
        const next = new Set();
        for (const id of prev) {
          if (newIds.has(id)) next.add(id);
          else changed = true;
        }
        return changed ? next : prev;
      });
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not load your inbox");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [folder, filter, search, filterCategoryId, filterLabelId]);

  const loadCategoriesAndLabels = useCallback(async () => {
    try {
      const [catRes, lblRes] = await Promise.all([
        api.get("/pro/categories"),
        api.get("/pro/labels"),
      ]);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setLabels(Array.isArray(lblRes.data) ? lblRes.data : []);
    } catch (err) {
      // Categories/labels aren't critical; log silently rather than block the UI.
      // eslint-disable-next-line no-console
      console.warn("Failed to load categories/labels", err);
    }
  }, []);

  const handleCreateCategory = useCallback(async (name) => {
    try {
      const { data } = await api.post("/pro/categories", { name });
      setCategories((prev) => [data, ...prev]);
      toast.success(`Category "${name}" created`);
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not create category");
    }
  }, []);

  const handleDeleteCategory = useCallback(async (categoryId) => {
    try {
      await api.delete(`/pro/categories/${categoryId}`);
      setCategories((prev) => prev.filter((c) => c.category_id !== categoryId));
      // Strip the deleted id from any open message / the list rows.
      setEmails((prev) => prev.map((m) => ({
        ...m,
        category_ids: (m.category_ids || []).filter((x) => x !== categoryId),
      })));
      setSelectedEmail((cur) => cur ? {
        ...cur,
        category_ids: (cur.category_ids || []).filter((x) => x !== categoryId),
      } : cur);
      toast.success("Category deleted");
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not delete category");
    }
  }, []);

  const handleCreateLabel = useCallback(async (name) => {
    try {
      const { data } = await api.post("/pro/labels", { name });
      setLabels((prev) => [data, ...prev]);
      toast.success(`Label "${name}" created`);
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not create label");
    }
  }, []);

  const handleDeleteLabel = useCallback(async (labelId) => {
    try {
      await api.delete(`/pro/labels/${labelId}`);
      setLabels((prev) => prev.filter((l) => l.label_id !== labelId));
      setEmails((prev) => prev.map((m) => ({
        ...m,
        label_ids: (m.label_ids || []).filter((x) => x !== labelId),
      })));
      setSelectedEmail((cur) => cur ? {
        ...cur,
        label_ids: (cur.label_ids || []).filter((x) => x !== labelId),
      } : cur);
      toast.success("Label deleted");
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not delete label");
    }
  }, []);

  // ── Poll for new messages every 8s while tab visible ──
  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadMessages({ silent: true });
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [loadMessages]);

  // ── Reload when folder/filter changes (and clear selection — spec'd UX). ──
  useEffect(() => {
    setSelectedIds(new Set());
    // Folder change resets category/label filter (different mailbox view).
    setFilterCategoryId(null);
    setFilterLabelId(null);
    loadMessages({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, filter]);

  // ── Reload when category/label filter changes (selection cleared). ──
  useEffect(() => {
    setSelectedIds(new Set());
    loadMessages({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategoryId, filterLabelId]);

  // ── Debounced search (also clears selection since the visible list changes) ──
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSelectedIds(new Set());
      loadMessages({ silent: true });
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openMessage = useCallback(async (email) => {
    setSelectedEmail({ ...email }); // optimistic
    setSelectedLoading(true);
    try {
      const { data } = await api.get(`/pro/messages/${email.email_id}`);
      setSelectedEmail(data);
      if (email.status === "unread") {
        setEmails((prev) =>
          prev.map((m) => (m.email_id === email.email_id ? { ...m, status: "read" } : m))
        );
        setCounts((c) => ({ ...c, unread: Math.max(0, (c.unread || 0) - 1) }));
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not open message");
      setSelectedEmail(null);
    } finally {
      setSelectedLoading(false);
    }
  }, []);

  // ── Message actions (archive, delete, star, spam, read-state, category/label) ──
  const handleAction = useCallback(async (email_id, action, categoryId, labelId) => {
    // Categories & labels — now fully supported via /api/pro/*.
    if (action === "add_category" || action === "remove_category") {
      if (!categoryId) return;
      try {
        if (action === "add_category") {
          await api.post(`/pro/categories/${categoryId}/assign/${email_id}`);
        } else {
          await api.delete(`/pro/categories/${categoryId}/assign/${email_id}`);
        }
        // Optimistic update
        const updater = (m) => {
          if (m.email_id !== email_id) return m;
          const cur = new Set(m.category_ids || []);
          if (action === "add_category") cur.add(categoryId);
          else cur.delete(categoryId);
          return { ...m, category_ids: Array.from(cur) };
        };
        setEmails((prev) => prev.map(updater));
        setSelectedEmail((cur) => cur ? updater(cur) : cur);
      } catch (err) {
        toast.error(getErrorMessage(err) || "Category action failed");
      }
      return;
    }
    if (action === "add_label" || action === "remove_label") {
      if (!labelId) return;
      try {
        if (action === "add_label") {
          await api.post(`/pro/labels/${labelId}/assign/${email_id}`);
        } else {
          await api.delete(`/pro/labels/${labelId}/assign/${email_id}`);
        }
        const updater = (m) => {
          if (m.email_id !== email_id) return m;
          const cur = new Set(m.label_ids || []);
          if (action === "add_label") cur.add(labelId);
          else cur.delete(labelId);
          return { ...m, label_ids: Array.from(cur) };
        };
        setEmails((prev) => prev.map(updater));
        setSelectedEmail((cur) => cur ? updater(cur) : cur);
      } catch (err) {
        toast.error(getErrorMessage(err) || "Label action failed");
      }
      return;
    }

    // Normalize EmailDetail's short read/unread actions to PRO backend names.
    const normalized = action === "read" ? "mark_read"
      : action === "unread" ? "mark_unread"
      : action;

    const supported = ["archive", "unarchive", "delete", "vip", "unvip", "spam", "not_spam", "mark_read", "mark_unread"];
    if (!supported.includes(normalized)) return;

    try {
      await api.post(`/pro/messages/${email_id}/action`, { action: normalized });

      // Optimistic UI
      if (normalized === "vip" || normalized === "unvip") {
        const is_vip = normalized === "vip";
        setEmails((prev) => prev.map((m) => m.email_id === email_id ? { ...m, is_vip } : m));
        setSelectedEmail((cur) => cur && cur.email_id === email_id ? { ...cur, is_vip } : cur);
      } else if (normalized === "mark_read" || normalized === "mark_unread") {
        // Stay in the current folder, flip the status in place.
        const newStatus = normalized === "mark_read" ? "read" : "unread";
        setEmails((prev) => prev.map((m) => m.email_id === email_id ? { ...m, status: newStatus } : m));
        setSelectedEmail((cur) => cur && cur.email_id === email_id ? { ...cur, status: newStatus } : cur);
      } else {
        // Folder-affecting actions: remove from current list + reload counts
        setEmails((prev) => prev.filter((m) => m.email_id !== email_id));
        if (selectedEmail?.email_id === email_id) setSelectedEmail(null);
      }
      // Pull fresh counts / list
      loadMessages({ silent: true });

      const labels = {
        archive: "Archived", unarchive: "Moved to inbox",
        delete: "Deleted", vip: "Starred", unvip: "Star removed",
        spam: "Marked as spam", not_spam: "Not spam",
        mark_read: "Marked as read", mark_unread: "Marked as unread",
      };
      toast.success(labels[normalized] || "Done");
    } catch (err) {
      toast.error(getErrorMessage(err) || "Action failed");
    }
  }, [selectedEmail, loadMessages]);

  // ── Bulk selection & bulk actions ─────────────────────────────────
  const toggleSelectOne = useCallback((emailId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId);
      else next.add(emailId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const allVisibleSelected = useMemo(() => {
    if (!emails.length) return false;
    return emails.every((m) => selectedIds.has(m.email_id));
  }, [emails, selectedIds]);

  // Star/unstar selection stats — drives the "show Star when any unstarred is
  // selected, show Unstar when any starred is selected" dynamic rule.
  const selectionStarStats = useMemo(() => {
    let starred = 0;
    let unstarred = 0;
    for (const m of emails) {
      if (!selectedIds.has(m.email_id)) continue;
      if (m.is_vip) starred++;
      else unstarred++;
    }
    return { starred, unstarred };
  }, [emails, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (emails.length && emails.every((m) => prev.has(m.email_id))) {
        return new Set();
      }
      return new Set(emails.map((m) => m.email_id));
    });
  }, [emails]);

  const runBulkAction = useCallback(async (action) => {
    if (bulkRunning) return;
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Actions that visually stay in the current folder vs. move rows out.
    // `unstar` moves rows out of the Starred folder specifically.
    const STAY_IN_PLACE_ACTIONS = new Set(["mark_read", "mark_unread", "star", "unstar"]);

    setBulkRunning(true);
    try {
      const { data } = await api.post("/pro/messages/bulk-action", {
        message_ids: ids,
        action,
      });

      const successSet = new Set(ids.filter((i) => !(data?.failed_ids || []).includes(i)));
      const effectiveFolder = filter === "starred" ? "starred" : folder;
      const stayInPlace = STAY_IN_PLACE_ACTIONS.has(action) && effectiveFolder !== "starred";

      // Decide whether to remove the acted-on rows from the current view.
      if (stayInPlace) {
        // Patch in-place: flip status or star state on the successful rows.
        setEmails((prev) => prev.map((m) => {
          if (!successSet.has(m.email_id)) return m;
          if (action === "mark_read") return { ...m, status: "read" };
          if (action === "mark_unread") return { ...m, status: "unread" };
          if (action === "star") return { ...m, is_vip: true };
          if (action === "unstar") return { ...m, is_vip: false };
          return m;
        }));
      } else {
        // Folder-changing actions (archive, delete, spam) — or unstar in Starred
        // folder — remove the acted-on rows.
        setEmails((prev) => prev.filter((m) => !successSet.has(m.email_id)));
        // If the currently-open message was removed, close the detail pane.
        if (selectedEmail && successSet.has(selectedEmail.email_id)) {
          setSelectedEmail(null);
        }
      }

      if (data?.counts) setCounts(data.counts);

      // Remove successful ids from selection; keep failed for retry.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of successSet) next.delete(id);
        return next;
      });

      const verb = {
        mark_read: "marked read",
        mark_unread: "marked unread",
        star: "starred",
        unstar: "unstarred",
        archive: "archived",
        delete: "deleted",
        spam: "moved to spam",
      }[action] || "updated";
      const ok = data?.success || successSet.size;
      const failed = data?.failed || 0;
      if (failed > 0) {
        toast.warning(`${ok} ${verb}, ${failed} failed`);
      } else {
        toast.success(`${ok} ${verb}`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || "Bulk action failed");
      loadMessages({ silent: true });
    } finally {
      setBulkRunning(false);
    }
  }, [bulkRunning, selectedIds, folder, filter, selectedEmail, loadMessages]);

  const copyAlias = useCallback(() => {
    if (!aliasAddress) return;
    navigator.clipboard.writeText(aliasAddress).then(
      () => toast.success("Alias copied"),
      () => toast.error("Could not copy"),
    );
  }, [aliasAddress]);

  const handleLogout = useCallback(async () => {
    try { await api.post("/pro/logout"); } catch { /* ignore */ }
    navigate("/pro/login", { replace: true });
  }, [navigate]);

  // ── Compose / Reply / Forward ──────────────────────────────────────
  const openCompose = useCallback(() => {
    setComposePrefill(null);
    setComposeOpen(true);
  }, []);

  const buildQuotedBody = (email) => {
    const when = email?.created_at ? new Date(email.created_at).toLocaleString() : "";
    const sender = email?.sender_name
      ? `${email.sender_name} <${email.sender_email}>`
      : email?.sender_email || "";
    const originalText = (email?.body || email?.body_preview || "").trim();
    const quoted = originalText
      .split("\n")
      .map((line) => `> ${line}`)
      .join("\n");
    return `\n\n\nOn ${when}, ${sender} wrote:\n${quoted}\n`;
  };

  const handleReply = useCallback((email) => {
    if (!email) return;
    const subj = email.subject || "";
    const prefillSubject = subj.toLowerCase().startsWith("re:") ? subj : `Re: ${subj}`;
    setComposePrefill({
      to: email.sender_email || "",
      subject: prefillSubject,
      body: buildQuotedBody(email),
      replyToMessageId: email.email_id,
    });
    setComposeOpen(true);
  }, []);

  const handleForward = useCallback((email) => {
    if (!email) return;
    const subj = email.subject || "";
    const prefillSubject = subj.toLowerCase().startsWith("fwd:") ? subj : `Fwd: ${subj}`;
    setComposePrefill({
      to: "",
      subject: prefillSubject,
      body: `\n\n---------- Forwarded message ----------\n${buildQuotedBody(email)}`,
      replyToMessageId: null,
    });
    setComposeOpen(true);
  }, []);

  const handleSent = useCallback(() => {
    // Refresh list so the message is marked read / sent message appears
    loadMessages({ silent: true });
  }, [loadMessages]);

  const visibleEmails = useMemo(() => {
    if (filter === "unread") return emails.filter((m) => m.status === "unread");
    return emails;
  }, [emails, filter]);

  if (loading && !emails.length) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center" data-testid="pro-inbox-loading">
        <Loader2 size={22} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  const currentFolderMeta = FOLDERS.find((f) => f.key === folder) || FOLDERS[0];
  const CurrentFolderIcon = currentFolderMeta.icon;

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="pro-inbox">
      {/* Header */}
      <header className="border-b border-[#E5E5E5] bg-white sticky top-0 z-20">
        <div className="max-w-[1440px] mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              className="xl:hidden -ml-1 p-1 rounded hover:bg-[#F5F5F5] text-zinc-600"
              onClick={() => setMobileNavOpen((v) => !v)}
              aria-label="Open navigation"
              data-testid="pro-inbox-mobile-menu"
            >
              <Menu size={18} />
            </button>
            <div className="w-8 h-8 rounded-lg bg-[var(--color-l-accent,#CC0000)] flex items-center justify-center shrink-0">
              <Shield size={16} className="text-white" />
            </div>
            <span className="font-semibold text-[#111111] text-sm truncate">QuickInbox PRO</span>
          </div>
          <div className="flex items-center gap-2">
            {aliasAddress && (
              <button
                type="button"
                onClick={copyAlias}
                className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[#E5E5E5] bg-white text-xs text-zinc-700 hover:bg-[#F5F5F5] font-mono max-w-[360px]"
                data-testid="pro-inbox-alias-chip"
                title="Copy your inbox address"
              >
                <span className="truncate">{aliasAddress}</span>
                <Copy size={12} className="text-zinc-400 shrink-0" />
              </button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/pro/dashboard")}
              className="text-zinc-500 hover:text-[#555555] text-xs gap-1.5"
              data-testid="pro-inbox-settings-btn"
            >
              <Settings size={14} /> <span className="hidden sm:inline">Account</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-500 hover:text-[#555555] text-xs gap-1.5"
              data-testid="pro-inbox-logout-btn"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Body: responsive layout
            • <md   (Mobile)   : 1-pane stacked (list ↔ detail with back btn)
            • md→xl (Tablet)   : 2-pane (list + detail), sidebar = drawer only
            • ≥xl   (Desktop)  : 3-pane (sidebar + list + detail) */}
      <main className="flex-1 max-w-[1440px] mx-auto w-full px-0 md:px-4 py-0 md:py-4">
        <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] xl:grid-cols-[220px_360px_1fr] gap-0 md:gap-4 h-[calc(100vh-3.5rem)] md:h-[calc(100vh-5rem)]">

          {/* Folder sidebar — desktop always, mobile via mobileNavOpen */}
          <aside
            className={`bg-white border-r xl:border border-[#E5E5E5] xl:rounded-xl shadow-sm flex-col overflow-hidden
              ${mobileNavOpen ? "absolute inset-0 z-30 flex" : "hidden xl:flex"}`}
            data-testid="pro-inbox-sidebar"
          >
            <div className="px-4 h-12 flex items-center justify-between border-b border-[#E5E5E5]">
              <span className="text-xs font-semibold text-[#555555] uppercase tracking-wide">Folders</span>
              <button
                type="button"
                className="xl:hidden text-zinc-500 text-xs"
                onClick={() => setMobileNavOpen(false)}
                data-testid="pro-inbox-close-nav"
              >
                Close
              </button>
            </div>
            <ScrollArea className="flex-1">
              <ul className="p-2 space-y-0.5">
                {FOLDERS.map((f) => {
                  const Icon = f.icon;
                  const count =
                    f.key === "inbox" ? counts.unread :
                    f.key === "starred" ? counts.starred :
                    f.key === "archived" ? counts.archived :
                    f.key === "spam" ? counts.spam :
                    f.key === "sent" ? counts.sent : 0;
                  const active = folder === f.key;
                  return (
                    <li key={f.key}>
                      <button
                        type="button"
                        onClick={() => {
                          setFolder(f.key);
                          setFilter("all");
                          setSelectedEmail(null);
                          setMobileNavOpen(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 h-9 rounded-lg text-sm transition-colors ${
                          active
                            ? "bg-[var(--color-l-accent-ring,#FBDDDD)] text-[var(--color-l-accent-ink,#7A0000)] font-medium"
                            : "text-[#333333] hover:bg-[#F5F5F5]"
                        }`}
                        data-testid={f.testId}
                      >
                        <Icon size={14} className="shrink-0" />
                        <span className="flex-1 text-left">{f.label}</span>
                        {count > 0 && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            active ? "bg-white text-[var(--color-l-accent-ink,#7A0000)]" : "bg-[#E5E5E5] text-[#555555]"
                          }`}>
                            {count}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Alias info card */}
              {aliasAddress && (
                <div className="m-2 mt-4 p-3 rounded-lg border border-dashed border-[#E5E5E5] bg-[#FAFAFA]">
                  <p className="text-[10px] uppercase tracking-wide text-[#888888] font-semibold mb-1">Your inbox</p>
                  <button
                    type="button"
                    onClick={copyAlias}
                    className="text-xs font-mono text-[#333333] break-all hover:text-[var(--color-l-accent,#CC0000)] text-left flex items-start gap-1.5"
                    data-testid="pro-inbox-sidebar-alias"
                  >
                    <span className="flex-1">{aliasAddress}</span>
                    <Copy size={11} className="text-zinc-400 mt-0.5 shrink-0" />
                  </button>
                </div>
              )}
            </ScrollArea>
          </aside>

          {/* Email list pane */}
          <section
            className={`bg-white md:border border-[#E5E5E5] md:rounded-xl shadow-sm flex-col overflow-hidden
              ${selectedEmail ? "hidden md:flex" : "flex"}`}
            data-testid="pro-inbox-list"
          >
            {/* List header — total height kept stable at 128px so the
                message list y-position never shifts when toggling between
                normal mode (title + search + filter chips) and bulk mode
                (2-row bulk action bar). */}
            <div className="border-b border-[#E5E5E5] min-h-[128px] flex flex-col">
              {selectedIds.size > 0 ? (
                <BulkActionBar
                  count={selectedIds.size}
                  allVisibleSelected={allVisibleSelected}
                  onToggleAll={toggleSelectAll}
                  onClear={clearSelection}
                  onAction={runBulkAction}
                  folder={filter === "starred" ? "starred" : folder}
                  running={bulkRunning}
                  starStats={selectionStarStats}
                />
              ) : (
              <>
                <div className="flex items-center justify-between px-4 h-12">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#111111]">
                    {filter === "starred" ? (
                      <>
                        <Star size={15} className="text-amber-500 fill-amber-400" /> Star
                        {counts.starred > 0 && (
                          <span className="ml-1 text-[10px] font-medium text-zinc-500 bg-[#F5F5F5] rounded-full px-1.5 py-0.5">
                            {counts.starred}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <CurrentFolderIcon size={15} /> {currentFolderMeta.label}
                        {counts.unread > 0 && folder === "inbox" && (
                          <span className="ml-1 text-[10px] font-medium text-white bg-[var(--color-l-accent,#CC0000)] rounded-full px-1.5 py-0.5" data-testid="pro-inbox-unread-badge">
                            {counts.unread}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => loadMessages({ silent: true })}
                      disabled={refreshing}
                      className="text-zinc-500 hover:text-[#555555] text-xs flex items-center gap-1 disabled:opacity-50"
                      data-testid="pro-inbox-refresh-btn"
                      title="Refresh"
                    >
                      <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
                      Refresh
                    </button>
                    <Button
                      size="sm"
                      onClick={openCompose}
                      className="bg-[var(--color-l-accent,#CC0000)] hover:bg-[var(--color-l-accent-h,#A80000)] text-white h-7 gap-1.5 px-2.5 text-xs"
                      data-testid="pro-inbox-compose-btn"
                    >
                      <PenLine size={12} /> Compose
                    </Button>
                  </div>
                </div>
                <div className="px-3 pb-2 pt-2">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search subject, sender, body…"
                      className="h-8 pl-7 text-xs bg-[#F9F9F9] border-[#E5E5E5]"
                      data-testid="pro-inbox-search"
                    />
                  </div>
                </div>
                <div className="px-3 pb-2 flex gap-1 flex-wrap min-h-[24px] items-center">
                  {folder !== "sent" && FILTERS.map((f) => {
                    const isActive = filter === f.key;
                    const showCount = f.key === "starred" && counts.starred > 0;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={`text-[11px] font-medium px-2.5 h-6 rounded-full transition-colors flex items-center gap-1 ${
                          isActive
                            ? "bg-[var(--color-l-accent,#CC0000)] text-white"
                            : "bg-[#F5F5F5] text-[#555555] hover:bg-[#ECECEC]"
                        }`}
                        data-testid={`pro-inbox-filter-${f.key}`}
                      >
                        {f.key === "starred" && (
                          <Star size={10} className={isActive ? "fill-white" : "fill-amber-400 text-amber-500"} />
                        )}
                        {f.label}
                        {showCount && (
                          <span className={`text-[10px] ${isActive ? "text-white/80" : "text-zinc-500"}`}>{counts.starred}</span>
                        )}
                      </button>
                    );
                  })}
                  {filterCategoryId && (() => {
                    const cat = categories.find((c) => c.category_id === filterCategoryId);
                    if (!cat) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setFilterCategoryId(null)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 h-6 rounded-full text-white"
                        style={{ backgroundColor: cat.color }}
                        title="Clear category filter"
                        data-testid="pro-inbox-active-filter-category"
                      >
                        Category: {cat.name} <X size={11} />
                      </button>
                    );
                  })()}
                  {filterLabelId && (() => {
                    const lbl = labels.find((l) => l.label_id === filterLabelId);
                    if (!lbl) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setFilterLabelId(null)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 h-6 rounded-full text-[#555555] bg-[#F5F5F5] border border-[#E5E5E5] hover:bg-[#ECECEC]"
                        title="Clear label filter"
                        data-testid="pro-inbox-active-filter-label"
                      >
                        Label: {lbl.name} <X size={11} />
                      </button>
                    );
                  })()}
                </div>
              </>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {visibleEmails.length === 0 ? (
                <ProEmptyState
                  folder={folder}
                  aliasAddress={aliasAddress}
                  filter={filter}
                  onCopy={copyAlias}
                />
              ) : (
                <ul className="divide-y divide-[#F0F0F0]">
                  {visibleEmails.map((m) => {
                    const isSel = selectedEmail?.email_id === m.email_id;
                    const isOutbound = m.direction === "outbound";
                    const isUnread = !isOutbound && m.status === "unread";
                    const isChecked = selectedIds.has(m.email_id);
                    const parsed = parseUTC(m.created_at);
                    const whenLabel = parsed
                      ? formatDistanceToNow(parsed, { addSuffix: true })
                      : "";
                    const primaryLabel = isOutbound
                      ? `To: ${m.to_email || "(unknown recipient)"}`
                      : (m.sender_name || m.sender_email || "(unknown sender)");
                    const hasSelection = selectedIds.size > 0;
                    return (
                      <li key={m.email_id}>
                        <div
                          className={`group relative w-full flex items-start gap-2 pl-2 pr-4 py-3 transition-colors ${
                            isSel ? "bg-[var(--color-l-accent-ring,#FBDDDD)]/60"
                            : isChecked ? "bg-[#FAFAFA]"
                            : "hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {/* Checkbox column — visible on hover, always when any row selected */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleSelectOne(m.email_id); }}
                            className={`shrink-0 w-6 h-6 flex items-center justify-center rounded transition-opacity ${
                              hasSelection || isChecked ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
                            }`}
                            aria-label={isChecked ? "Deselect" : "Select"}
                            aria-checked={isChecked}
                            role="checkbox"
                            data-testid={`pro-inbox-checkbox-${m.email_id}`}
                          >
                            {isChecked ? (
                              <CheckSquare size={16} className="text-[var(--color-l-accent,#CC0000)]" />
                            ) : (
                              <Square size={16} className="text-zinc-400" />
                            )}
                          </button>

                          {/* Clickable content area */}
                          <button
                            type="button"
                            onClick={() => openMessage(m)}
                            className="flex-1 min-w-0 flex gap-3 text-left"
                            data-testid={`pro-inbox-row-${m.email_id}`}
                          >
                            <div className="shrink-0 flex flex-col items-center gap-1 pt-1">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  isUnread ? "bg-[var(--color-l-accent,#CC0000)]" : "bg-transparent"
                                }`}
                                aria-hidden="true"
                              />
                              {m.is_vip && (
                                <Star size={12} className="text-amber-500 fill-amber-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2">
                                <p className={`text-sm truncate ${isUnread ? "font-semibold text-[#111111]" : "text-[#333333]"}`}>
                                  {primaryLabel}
                                </p>
                                {whenLabel && (
                                  <span className="text-[11px] text-zinc-400 shrink-0 whitespace-nowrap">
                                    {whenLabel}
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs truncate mt-0.5 ${isUnread ? "text-[#111111]" : "text-zinc-600"}`}>
                                {m.subject || "(no subject)"}
                              </p>
                              {m.body_preview && (
                                <p className="text-[11px] text-zinc-400 truncate mt-0.5">{m.body_preview}</p>
                              )}
                              {/* Category/Label pills — skipped for outbound (Sent) messages
                                  since they don't carry metadata in this MVP.
                                  Clickable: act as filter shortcuts (stop propagation so the row's
                                  open-message click isn't triggered). */}
                              {!isOutbound && ((m.category_ids?.length > 0) || (m.label_ids?.length > 0)) && (
                                <div className="flex items-center gap-1 flex-wrap mt-1.5" data-testid={`pro-inbox-row-badges-${m.email_id}`}>
                                  {(m.category_ids || []).map((id) => {
                                    const cat = categories.find((c) => c.category_id === id);
                                    if (!cat) return null;
                                    const label = cat.name.length > 10 ? cat.name.slice(0, 10) + "…" : cat.name;
                                    return (
                                      <span
                                        key={`c-${id}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); setFilterCategoryId(id); setFilterLabelId(null); }}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setFilterCategoryId(id); setFilterLabelId(null); } }}
                                        title={`Filter by category: ${cat.name}`}
                                        className="inline-flex items-center text-[10px] font-medium text-white px-1.5 py-0.5 rounded cursor-pointer hover:opacity-90"
                                        style={{ backgroundColor: cat.color }}
                                      >
                                        {label}
                                      </span>
                                    );
                                  })}
                                  {(m.label_ids || []).map((id) => {
                                    const lbl = labels.find((l) => l.label_id === id);
                                    if (!lbl) return null;
                                    const label = lbl.name.length > 10 ? lbl.name.slice(0, 10) + "…" : lbl.name;
                                    return (
                                      <span
                                        key={`l-${id}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); setFilterLabelId(id); setFilterCategoryId(null); }}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setFilterLabelId(id); setFilterCategoryId(null); } }}
                                        title={`Filter by label: ${lbl.name}`}
                                        className="inline-flex items-center text-[10px] text-[#555555] bg-[#F5F5F5] border border-[#E5E5E5] px-1.5 py-0.5 rounded cursor-pointer hover:bg-[#ECECEC]"
                                      >
                                        {label}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* Detail pane — reuses EmailDetail from /inbox */}
          <section
            className={`bg-white md:border border-[#E5E5E5] md:rounded-xl shadow-sm overflow-hidden flex-col
              ${selectedEmail ? "flex" : "hidden md:flex"}`}
            data-testid="pro-inbox-detail"
          >
            {selectedEmail ? (
              <>
                {/* Mobile back bar (only <md, since md+ shows the 2-pane layout) */}
                <div className="md:hidden flex items-center gap-2 px-3 h-11 border-b border-[#E5E5E5] bg-white">
                  <button
                    type="button"
                    onClick={() => setSelectedEmail(null)}
                    className="p-1 -ml-1 rounded text-zinc-500 hover:bg-[#F5F5F5]"
                    data-testid="pro-inbox-back-btn"
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#111111] truncate">
                      {selectedEmail.subject || "(no subject)"}
                    </p>
                    <p className="text-[10px] text-[#888888] truncate">
                      {selectedEmail.sender_name || selectedEmail.sender_email}
                    </p>
                  </div>
                </div>
                {selectedLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 size={20} className="animate-spin text-zinc-500" />
                  </div>
                ) : (
                  <EmailDetail
                    email={selectedEmail}
                    onAction={handleAction}
                    categories={categories}
                    labels={labels}
                    folder={
                      folder === "archived" ? "archive" :
                      folder === "spam" ? "spam" :
                      folder === "sent" ? "sent" : "inbox"
                    }
                    onClose={() => setSelectedEmail(null)}
                    onReply={handleReply}
                    onForward={handleForward}
                    onCreateCategory={handleCreateCategory}
                    onDeleteCategory={handleDeleteCategory}
                    onCreateLabel={handleCreateLabel}
                    onDeleteLabel={handleDeleteLabel}
                  />
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center px-6 text-center space-y-2" data-testid="pro-inbox-detail-empty">
                <MailOpen size={24} className="text-zinc-300" />
                <p className="text-sm text-zinc-500">Select a message to read it.</p>
              </div>
            )}
          </section>
        </div>
      </main>

      <ProComposeEmail
        open={composeOpen}
        onOpenChange={setComposeOpen}
        aliasAddress={aliasAddress}
        prefill={composePrefill}
        onSent={handleSent}
      />
    </div>
  );
}

function ProEmptyState({ folder, aliasAddress, filter, onCopy }) {
  if (filter === "unread") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
        <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center">
          <MailOpen size={20} className="text-zinc-400" />
        </div>
        <p className="text-sm font-medium text-[#111111]">No unread messages</p>
        <p className="text-xs text-zinc-500">You're all caught up.</p>
      </div>
    );
  }
  if (folder === "sent") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
        <Send size={22} className="text-zinc-300" />
        <p className="text-sm font-medium text-[#111111]">No sent emails</p>
        <p className="text-xs text-zinc-500">Emails you send will appear here.</p>
      </div>
    );
  }
  if (folder === "archived") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
        <Archive size={22} className="text-zinc-300" />
        <p className="text-sm font-medium text-[#111111]">No archived emails</p>
        <p className="text-xs text-zinc-500">Archived messages will appear here.</p>
      </div>
    );
  }
  if (folder === "spam") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
        <AlertTriangle size={22} className="text-zinc-300" />
        <p className="text-sm font-medium text-[#111111]">No spam</p>
        <p className="text-xs text-zinc-500">All clear.</p>
      </div>
    );
  }
  if (folder === "starred") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
        <Star size={22} className="text-zinc-300" />
        <p className="text-sm font-medium text-[#111111]">No starred messages</p>
        <p className="text-xs text-zinc-500">Star a message to keep it close.</p>
      </div>
    );
  }
  // inbox
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 space-y-3" data-testid="pro-inbox-empty">
      <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center">
        <MailOpen size={20} className="text-zinc-400" />
      </div>
      <div>
        <p className="text-sm font-medium text-[#111111]">Your inbox is empty</p>
        <p className="text-xs text-zinc-500 mt-1">Share your address to start receiving emails.</p>
      </div>
      {aliasAddress && (
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E5E5E5] bg-white text-xs text-zinc-700 hover:bg-[#F5F5F5] font-mono"
          data-testid="pro-inbox-empty-alias"
        >
          {aliasAddress} <Copy size={12} className="text-zinc-400" />
        </button>
      )}
    </div>
  );
}

// ── BulkActionBar ─────────────────────────────────────────────────────
// Stable 2-row layout to avoid horizontal overflow / scrollbar / vertical
// shifts:
//   • Row 1 (h-12): select-all checkbox + "N selected" count + clear (✕)
//   • Row 2 (flex-1): action icons, evenly distributed (justify-between),
//     icon-only so all of them fit in the narrow 360px list column.
// Total height is locked to `min-h-[128px]` matching the normal-mode header
// (title h-12 + search 48px + filter chips 32px ≈ 128px) so toggling
// selection never moves the message list.
function BulkActionBar({ count, allVisibleSelected, onToggleAll, onClear, onAction, folder, running, starStats }) {
  const isSent = folder === "sent";

  // Per-folder action set:
  //   - Sent: only delete.
  //   - All other folders: read/unread + star/unstar + Move to… + delete.
  // The Archive / Spam / Not-spam icon buttons have been replaced by a single
  // "Move to…" dropdown which renders only the valid destinations for the
  // current folder (see MoveToMenu).
  const showReadState = !isSent;
  const showMoveTo = !isSent;

  // Map current PRO folder key → MoveToMenu folder vocabulary.
  // PRO uses "archived" while MoveToMenu accepts "archive" or "archived" — keep
  // the value untouched here; the menu normalises both.
  const moveFolder = folder;

  // Dynamic Star/Unstar based on what's selected (spec rule).
  const hasStarred = (starStats?.starred ?? 0) > 0;
  const hasUnstarred = (starStats?.unstarred ?? 0) > 0;
  const showStar = !isSent && hasUnstarred;
  const showUnstar = !isSent && hasStarred;

  const act = (a) => {
    if (!running) onAction(a);
  };

  const IconBtn = ({ onClick, title, testId, children, accent }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={running}
      className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        accent === "danger"
          ? "text-red-600 hover:bg-red-50"
          : "text-zinc-600 hover:bg-white hover:text-[#111111]"
      }`}
      title={title}
      aria-label={title}
      data-testid={testId}
    >
      {children}
    </button>
  );

  return (
    <div
      className="bg-[var(--color-l-accent-ring,#FBDDDD)]/50 flex flex-col min-h-[128px]"
      data-testid="pro-inbox-bulk-bar"
    >
      {/* Row 1 — select-all + count + clear */}
      <div className="h-12 px-3 flex items-center justify-between gap-2 border-b border-[#E5E5E5]/60">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onToggleAll}
            disabled={running}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-white/60 transition-colors"
            aria-label={allVisibleSelected ? "Deselect all" : "Select all"}
            data-testid="pro-inbox-bulk-select-all"
          >
            {allVisibleSelected ? (
              <CheckSquare size={16} className="text-[var(--color-l-accent,#CC0000)]" />
            ) : (
              <Square size={16} className="text-zinc-500" />
            )}
          </button>
          <span className="text-xs font-medium text-[#111111] truncate" data-testid="pro-inbox-bulk-count">
            {count} selected
          </span>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={running}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded hover:bg-white/60 text-zinc-500 transition-colors"
          aria-label="Clear selection"
          data-testid="pro-inbox-bulk-clear"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
        </button>
      </div>

      {/* Row 2 — action icons. flex-1 fills remaining space (~80px) and items
          are vertically centered, distributed so they always remain visible
          without horizontal scroll. */}
      <div className="flex-1 px-3 flex items-center justify-between gap-1">
        {showReadState && (
          <IconBtn onClick={() => act("mark_read")} title="Mark as read" testId="pro-inbox-bulk-mark-read">
            <MailOpen size={16} />
          </IconBtn>
        )}
        {showReadState && (
          <IconBtn onClick={() => act("mark_unread")} title="Mark as unread" testId="pro-inbox-bulk-mark-unread">
            <Mail size={16} />
          </IconBtn>
        )}
        {showStar && (
          <IconBtn onClick={() => act("star")} title="Star" testId="pro-inbox-bulk-star">
            <Star size={16} />
          </IconBtn>
        )}
        {showUnstar && (
          <IconBtn onClick={() => act("unstar")} title="Unstar" testId="pro-inbox-bulk-unstar">
            <StarOff size={16} />
          </IconBtn>
        )}
        {showMoveTo && (
          <MoveToMenu
            folder={moveFolder}
            onAction={act}
            variant="icon"
            testIdPrefix="pro-inbox-bulk-move-to"
            disabled={running}
          />
        )}
        <IconBtn onClick={() => act("delete")} title="Delete" testId="pro-inbox-bulk-delete" accent="danger">
          <Trash2 size={16} />
        </IconBtn>
      </div>
    </div>
  );
}

